import React, {
  useCallback,
  useEffect,
  useState
} from 'react';

import {
  get,
  put
} from '../utils/api';

const ACTIVE_STATUSES = [
  'checked_in',
  'admitted',
  'meeting'
];

const readable = value =>
  String(value || '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, letter =>
      letter.toUpperCase()
    );

const formatDate = value => {
  if (!value) return 'Not recorded';

  return new Date(value).toLocaleString(
    'en-NG',
    {
      dateStyle: 'medium',
      timeStyle: 'short'
    }
  );
};

export default function MyVisitors() {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadVisitors = useCallback(async () => {
    try {
      setError('');

      const response = await get(
        '/front-desk/my-visitors'
      );

      setVisitors(response?.data || []);
    } catch (err) {
      setError(
        err.message ||
        'Your visitors could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVisitors();

    const interval = window.setInterval(
      loadVisitors,
      30000
    );

    return () =>
      window.clearInterval(interval);
  }, [loadVisitors]);

  const respond = async (
    visitor,
    responseStatus
  ) => {
    let responseNote = '';

    if (
      responseStatus === 'declined' ||
      responseStatus === 'unavailable'
    ) {
      const entered = window.prompt(
        responseStatus === 'declined'
          ? 'Why are you declining this visit?'
          : 'Why are you unavailable?',
        ''
      );

      if (entered === null) return;

      responseNote = entered.trim();

      if (!responseNote) {
        setError(
          'A short explanation is required.'
        );
        return;
      }
    }

    const confirmed = window.confirm(
      responseStatus === 'acknowledged'
        ? `Confirm that you will receive ${visitor.visitor_name}?`
        : `Confirm ${readable(responseStatus)} for ${visitor.visitor_name}?`
    );

    if (!confirmed) return;

    try {
      setSavingId(visitor.id);
      setError('');
      setNotice('');

      await put(
        `/front-desk/visits/${visitor.id}/host-response`,
        {
          response_status: responseStatus,
          response_note: responseNote || null
        }
      );

      setNotice(
        `Your response for ${visitor.visitor_name} was recorded.`
      );

      await loadVisitors();
    } catch (err) {
      setError(
        err.message ||
        'Your response could not be recorded.'
      );
    } finally {
      setSavingId(null);
    }
  };

  const activeVisitors = visitors.filter(
    visitor =>
      ACTIVE_STATUSES.includes(
        visitor.arrival_status
      )
  );

  const previousVisitors = visitors.filter(
    visitor =>
      !ACTIVE_STATUSES.includes(
        visitor.arrival_status
      )
  );

  const renderVisitor = visitor => {
    const canRespond =
      ACTIVE_STATUSES.includes(
        visitor.arrival_status
      ) &&
      visitor.host_response_status === 'pending';

    return (
      <div
        key={visitor.id}
        className="card"
        style={{
          marginBottom: 16,
          borderLeft:
            canRespond
              ? '5px solid #A8893D'
              : '5px solid #556B2F'
        }}
      >
        <div
          className="card-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap'
          }}
        >
          <strong>{visitor.visitor_name}</strong>

          <span
            style={{
              fontWeight: 600,
              color:
                canRespond
                  ? '#8F2E24'
                  : '#556B2F'
            }}
          >
            {readable(
              visitor.host_response_status
            )}
          </span>
        </div>

        <div
          style={{
            padding: 18,
            display: 'grid',
            gap: 10
          }}
        >
          <div>
            <strong>Visit reference:</strong>{' '}
            {visitor.visit_ref}
          </div>

          <div>
            <strong>Arrival:</strong>{' '}
            {formatDate(visitor.arrived_at)}
          </div>

          <div>
            <strong>Visit status:</strong>{' '}
            {readable(visitor.arrival_status)}
          </div>

          <div>
            <strong>Purpose:</strong>{' '}
            {visitor.purpose || 'Not provided'}
          </div>

          <div>
            <strong>Organisation:</strong>{' '}
            {visitor.organisation || 'Not provided'}
          </div>

          <div>
            <strong>Access requested:</strong>{' '}
            {readable(visitor.access_level)}
          </div>

          <div>
            <strong>Visit nature:</strong>{' '}
            {readable(
              visitor.visit_nature ||
              visitor.visitor_category ||
              'official'
            )}
          </div>

          {canRespond && (
            <div
              style={{
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
                marginTop: 8
              }}
            >
              <button
                type="button"
                className="btn btn-primary"
                disabled={savingId === visitor.id}
                onClick={() =>
                  respond(
                    visitor,
                    'acknowledged'
                  )
                }
              >
                I Will Receive Visitor
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                disabled={savingId === visitor.id}
                onClick={() =>
                  respond(
                    visitor,
                    'unavailable'
                  )
                }
              >
                I Am Unavailable
              </button>

              <button
                type="button"
                className="btn btn-danger"
                disabled={savingId === visitor.id}
                onClick={() =>
                  respond(
                    visitor,
                    'declined'
                  )
                }
              >
                Decline Visit
              </button>
            </div>
          )}

          {!canRespond &&
           visitor.host_response_status !==
             'pending' && (
            <div
              style={{
                marginTop: 6,
                color: '#556B2F',
                fontWeight: 600
              }}
            >
              Your response has been recorded.
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        Loading your visitors...
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ marginBottom: 6 }}>
          My Visitors
        </h1>

        <p style={{ margin: 0, color: '#666' }}>
          Visitors assigned directly to you.
          This page refreshes every 30 seconds.
        </p>
      </div>

      {error && (
        <div
          className="alert alert-danger"
          style={{ marginBottom: 16 }}
        >
          {error}
        </div>
      )}

      {notice && (
        <div
          className="alert alert-success"
          style={{ marginBottom: 16 }}
        >
          {notice}
        </div>
      )}

      <section>
        <h2>
          Active Visitors ({activeVisitors.length})
        </h2>

        {activeVisitors.length
          ? activeVisitors.map(renderVisitor)
          : (
            <div className="card">
              <div style={{ padding: 18 }}>
                You have no active visitors.
              </div>
            </div>
          )}
      </section>

      <section style={{ marginTop: 28 }}>
        <h2>Previous Visitors</h2>

        {previousVisitors.length
          ? previousVisitors.map(renderVisitor)
          : (
            <div className="card">
              <div style={{ padding: 18 }}>
                No previous visitor records.
              </div>
            </div>
          )}
      </section>
    </div>
  );
}
